import { of as observableOf, Observable, Subject, timer } from 'rxjs';
import { Injectable } from '@angular/core';
import { mergeMap, map, takeWhile, takeUntil, switchMap, filter, take } from 'rxjs/operators';
import {
  HttpClient,
  HttpHeaders,
  HttpBackend,
  HttpResponse,
  HttpRequest,
  HttpEventType
} from '@angular/common/http';
import { HttpRequestOptions } from '../../core/http-request-options';
import { TokenCache } from '../../security/token-cache';
import { environment } from '../../environment';
import { FileDownloadResult } from '../../core/models/file-download-result';
import { FileDownloadService } from '../../core/file-download/file-download.service';
import { ServiceLoadingResponse } from './service-loading-response';

const POLL_INTERVAL_MS = 10_000;
const POLL_TIMEOUT_MS  = 600_000; // 10 minutes
const MAX_POLL_TICKS   = POLL_TIMEOUT_MS / POLL_INTERVAL_MS; // 60 ticks

@Injectable()
export class ReportingHttpService extends HttpClient {
  headers: HttpHeaders;
  postOptions: HttpRequestOptions;

  // Saved reference to HttpBackend so getS3File can bypass the interceptor
  // chain entirely — presigned S3 URLs must not receive an Authorization header.
  private httpBackend: HttpBackend;

  protected _isLoading = new Subject<ServiceLoadingResponse>();
  public get isLoading() {
    return this._isLoading.asObservable();
  }

  constructor(
    backend: HttpBackend,
    options: HttpRequestOptions,
    private tokenCache: TokenCache,
    private fileService: FileDownloadService
  ) {
    super(backend);
    this.httpBackend = backend; // saved for direct S3 requests
    this.setHeaders();
    this.tokenCache.onTokenChange.subscribe(() => this.setHeaders());
  }

  // ─── HTTP helpers ─────────────────────────────────────────────────────────

  public get(url: string, options: any): Observable<any> {
    options = options || new HttpRequestOptions();
    if (!options.headers) {
      options.headers = this.headers;
    }
    options = this.getRequestOption(options);
    url = `${environment.REPORTING_API_BASE}${url}`;
    return super.get(url, options);
  }

  public getFile(url: string, options: any): Observable<any> {
    if (!options) {
      const newHeaders = this.headers.append('Accept', 'application/*');
      options = { headers: newHeaders, responseType: 'blob', observe: 'response' };
    }
    url = `${environment.REPORTING_API_BASE}${url}`;
    return super.get(url, options);
  }

  /**
   * Downloads a file from an AWS S3 presigned URL.
   *
   * WHY HttpBackend instead of window.open() or HttpClient:
   *  - window.open() is blocked by popup blockers when called from an async
   *    RxJS timer callback (not triggered by a direct user gesture).
   *  - HttpClient attaches our Authorization header to every request. S3
   *    presigned URLs are self-contained credentials — a second auth header
   *    causes SignatureDoesNotMatch from AWS.
   *  - HttpBackend bypasses Angular's entire interceptor chain so no
   *    Authorization header is added. This is the Angular-intended escape
   *    hatch for exactly this scenario.
   *  - The response is a real Blob, so SaveFileToBrowser works normally.
   */
  public getS3File(url: string, fileName: string): Observable<FileDownloadResult> {
    const req = new HttpRequest('GET', url, {
      responseType: 'blob',
      reportProgress: false
    });

    return new Observable(observer => {
      this.httpBackend.handle(req).subscribe(
        event => {
          if (event.type === HttpEventType.Response) {
            const response = event as HttpResponse<Blob>;
            const result = new FileDownloadResult();
            result.fileName = fileName;
            result.fileContent = response.body;
            observer.next(result);
            observer.complete();
          }
        },
        error => observer.error(error)
      );
    });
  }

  public getFileWithParams(url: string, body: any, options: any): Observable<Object> {
    if (!options) {
      const newHeaders = this.headers.append('Accept', 'application/*');
      options = { headers: newHeaders, responseType: 'blob', observe: 'response' };
    }
    url = `${environment.REPORTING_API_BASE}${url}`;
    return super.put(url, body, options);
  }

  public post(url: string, body: any, options?: any): Observable<any> {
    url = `${environment.REPORTING_API_BASE}${url}`;
    return super.post(url, body, this.getRequestOption(this.postOptions));
  }

  // ─── Polling ──────────────────────────────────────────────────────────────

  /**
   * Polls every 10 s for up to 10 minutes using RxJS timer().
   *
   * Two parallel streams are used because takeWhile is EXCLUSIVE in RxJS 6.x
   * (the inclusive flag has a known bug in 6.5.x and is avoided):
   *  - poll$     : keeps firing while server returns retry:true, used only
   *                to detect the 10-minute timeout via its complete callback.
   *  - terminal$ : filters for the first non-retry response and completes.
   *
   * downloadSucceeded bridges the two streams so the poll$ complete handler
   * does not emit a false timeout after a successful download.
   */
  /**
   * Polls every 10 s for up to 10 minutes using a SINGLE shared stream.
   *
   * Previous approach used two independent timer() streams (poll$ + terminal$)
   * which caused a race: poll$'s complete fired before terminal$'s next(),
   * so `done` was never true in time and a false timeout was emitted.
   *
   * Fix: one tick$ stream, one subscribe(). All logic — result handling AND
   * timeout detection — lives in the same subscriber so ordering is guaranteed.
   * No race, no duplicate HTTP requests, no microtask workarounds needed.
   */
  public DownloadReport(reportType: string, key: string, timeEntry: number): Observable<any> {
    this._isLoading.next({ loading: true, reportType });

    const result$ = new Subject<any>();
    const stop$   = new Subject<void>();  // ← signals tick$ to stop immediately

    let done = false;

    const tick$ = timer(0, POLL_INTERVAL_MS).pipe(
      take(MAX_POLL_TICKS),
      takeUntil(stop$),
      // switchMap cancels the previous in-flight HTTP request before starting
      // the next tick's request. mergeMap was launching new concurrent requests
      // without cancelling previous ones, causing multiple overlapping API calls
      // when the server took longer than POLL_INTERVAL_MS to respond.
      switchMap(() => this.AttemptGetFile(reportType, key))
    );

    tick$.subscribe(
      response => {
        if (response.retry) {
          return; // not ready yet — wait for next tick
        }
        done = true;
        stop$.next();   // ← cancel all future ticks immediately
        stop$.complete();
        if (response instanceof FileDownloadResult) {
          this.fileService.SaveFileToBrowser(response);
          this._isLoading.next({ loading: false, reportType });
        } else {
          this.handleError('File not found.');
          this._isLoading.next({ loading: false, reportType });
        }
        result$.next(response);
        result$.complete();
      },
      error => {
        done = true;
        stop$.next();
        stop$.complete();
        this.handleError(error);
        this._isLoading.next({ loading: false, reportType });
        result$.error(error);
      },
      () => {
        if (!done) {
          this._isLoading.next({ loading: false, reportType, timeout: true });
        }
        result$.complete();
      }
    );

    return result$.asObservable();
  }

  /**
   * Same single-stream polling pattern as DownloadReport but reads JSON
   * from the blob response instead of saving a file.
   */
  protected GetReportData(reportType: string, key: string, timeEntry: number): Observable<any> {
    this._isLoading.next({ loading: true, reportType });

    const result$ = new Subject<any>();
    const stop$   = new Subject<void>();

    let done = false;

    const tick$ = timer(0, POLL_INTERVAL_MS).pipe(
      take(MAX_POLL_TICKS),
      takeUntil(stop$),
      switchMap(() => this.AttemptGetReportData(reportType, key))
    );

    tick$.subscribe(
      response => {
        if (response.retry) {
          return; // not ready yet — wait for next tick
        }
        done = true;
        stop$.next();
        stop$.complete();
        const reader = new FileReader();
        reader.addEventListener('loadend', (e) => {
          const fileData = (e.target as FileReader).result as string;
          this._isLoading.next({
            loading: false,
            reportType,
            fileData: JSON.parse(fileData)
          });
        });
        reader.readAsText(response.body);
        result$.next(response);
        result$.complete();
      },
      error => {
        done = true;
        stop$.next();
        stop$.complete();
        this.handleError(error);
        this._isLoading.next({ loading: false, reportType });
        result$.error(error);
      },
      () => {
        if (!done) {
          this._isLoading.next({ loading: false, reportType, timeout: true });
        }
        result$.complete();
      }
    );

    return result$.asObservable();
  }

  // ─── Private helpers ──────────────────────────────────────────────────────

  private AttemptGetFile(reportType: string, key: string): Observable<any> {
    const options = new HttpRequestOptions();
    options.observe = 'response';
    options.responseType = 'json';

    return this.get(`/reports/file/${reportType}/${key}`, options).pipe(
      map<HttpResponse<any>, any>(result => {
        if (result.status === 200) {
          if (result.body.downloadLink) {
            return {
              retry: false,
              downloadLink: result.body.downloadLink,
              fileName: result.body.fileName
            };
          }
          this.handleError('File not found.');
          return { retry: false };
        }
        return { retry: true };
      }),
      mergeMap(response => {
        if (!response.downloadLink) {
          return observableOf(response);
        }
        // HttpBackend path: fetches the blob directly from S3 without
        // attaching our Authorization header.
        return this.getS3File(response.downloadLink, response.fileName);
      })
    );
  }

  private AttemptGetReportData(reportType: string, key: string): Observable<any> {
    return this.getFile(`/reports/report/${reportType}/${key}`, null).pipe(
      map(result => (result.status === 200 ? result : { retry: true }))
    );
  }

  protected handleError(error: any): Promise<any> {
    console.error('An error occurred', error);
    return Promise.reject(error.message || error);
  }

  private setHeaders() {
    this.headers = new HttpHeaders({
      Authorization: this.tokenCache.getBearerToken()
    });

    const option = new HttpRequestOptions();
    option.headers = new HttpHeaders({
      Authorization: this.tokenCache.getBearerToken(),
      'Content-Type': 'application/json'
    });
    this.postOptions = option;
  }

  private getRequestOption(option: HttpRequestOptions): any {
    return {
      headers: option.headers,
      observe: option.observe,
      params: option.params,
      reportProgress: option.reportProgress,
      responseType: option.responseType,
      withCredentials: option.withCredentials
    };
  }
}
